import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router';
import { LayoutDashboard, Package, Image, Languages, Settings, LogOut, Home, MessageSquare, ShoppingBag, Mail, FileText, Car, Link2, Menu, X, Users, Download, Camera, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { useAdmin } from '../context/AdminContext';
import { useNavigate } from 'react-router';

const LOGO_URL = '/logo.png';

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, adminUser } = useAdmin();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const menuItems = [
    { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
    { name: 'Capture', path: '/admin/capture', icon: Camera },
    { name: 'Staff', path: '/admin/staff', icon: ShieldCheck },
    { name: 'Products', path: '/admin/products', icon: Package },
    { name: 'Gallery', path: '/admin/gallery', icon: Image },
    { name: 'Testimonials', path: '/admin/testimonials', icon: MessageSquare },
    { name: 'Orders', path: '/admin/orders', icon: ShoppingBag },
    { name: 'Users', path: '/admin/users', icon: Users },
    { name: 'Messages', path: '/admin/contacts', icon: Mail },
    { name: 'Pages', path: '/admin/pages', icon: FileText },
    { name: 'Brands', path: '/admin/brands', icon: Car },
    { name: 'Downloads', path: '/admin/downloads', icon: Download },
    { name: 'Redirects', path: '/admin/redirects', icon: Link2 },
    { name: 'Translations', path: '/admin/translations', icon: Languages },
    { name: 'Settings', path: '/admin/settings', icon: Settings },
  ];

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 w-64 bg-gray-900 text-white z-50 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="admin-sidebar"
      >
        <div className="p-4 sm:p-6 flex flex-col h-full">
          <div className="mb-6 sm:mb-8 flex items-center justify-between">
            <div>
              <img src={LOGO_URL} alt="iTRONiX Admin" className="h-8 sm:h-10 w-auto brightness-0 invert" />
              <div className="text-xs text-gray-400 mt-1 sm:mt-2">Admin Control Panel</div>
            </div>
            <button
              className="lg:hidden text-gray-400 hover:text-white p-1"
              onClick={() => setSidebarOpen(false)}
              data-testid="close-sidebar-btn"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <nav className="space-y-1 flex-1 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                    isActive
                      ? 'bg-red-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>

          <div className="pt-4 border-t border-gray-800 mt-4">
            <div className="mb-3">
              <div className="text-sm font-medium">{adminUser?.username}</div>
              <div className="text-xs text-gray-400">{adminUser?.role}</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full border-gray-700 text-gray-300 hover:bg-gray-800"
              onClick={handleLogout}
              data-testid="admin-logout-btn"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
            <Link to="/">
              <Button
                variant="ghost"
                size="sm"
                className="w-full mt-2 text-gray-400 hover:text-white"
              >
                <Home className="mr-2 h-4 w-4" />
                Back to Website
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:ml-64 min-h-screen">
        {/* Top Bar */}
        <div className="bg-white shadow-sm border-b sticky top-0 z-30">
          <div className="px-4 sm:px-8 py-3 sm:py-4 flex items-center gap-3">
            <button
              className="lg:hidden text-gray-700 hover:text-red-600 p-1"
              onClick={() => setSidebarOpen(true)}
              data-testid="open-sidebar-btn"
            >
              <Menu className="h-6 w-6" />
            </button>
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900">Control Panel</h1>
          </div>
        </div>
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
